import { useMemo, useState } from "react";
import { gossipList, categories } from "@/mocks/gossip";
import type { GossipItem } from "@/mocks/gossip";

export default function FeedSection() {
  const [activeCat, setActiveCat] = useState("全部");
  const [query, setQuery] = useState("");
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [visible, setVisible] = useState(6);

  const cats = ["全部", ...categories];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return gossipList.filter((g) => {
      const matchCat = activeCat === "全部" || g.category === activeCat;
      const matchQ = !q || g.title.toLowerCase().includes(q) || g.desc.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [activeCat, query]);

  const shown = filtered.slice(0, visible);

  const toggleLike = (g: GossipItem) => {
    setLiked((prev) => {
      const next = { ...prev };
      next[g.id] = !prev[g.id];
      return next;
    });
  };

  return (
    <section id="feed" data-anchor className="bg-background-50">
      <div className="mx-auto w-full max-w-6xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <p className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-primary-600">
              <i className="ri-water-flash-line text-base" aria-hidden="true" />
              现场围观
            </p>
            <h2 className="text-2xl font-black text-foreground-950 md:text-4xl">今日热瓜现场</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              各路瓜友正在实时讨论的话题都在这里，点进评论区就能见，随时加入不冷场。
            </p>
          </div>

          <label className="relative block w-full md:w-72">
            <span className="sr-only">筛选热瓜</span>
            <i
              className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setVisible(6);
              }}
              placeholder="搜话题、搜关键词…"
              className="w-full rounded-full border border-background-300 bg-background-100/60 py-2.5 pl-10 pr-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400 focus:ring-2 focus:ring-accent-300/50"
            />
          </label>
        </div>

        <div className="mt-7 flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setActiveCat(c);
                setVisible(6);
              }}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                activeCat === c
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-background-300 bg-background-100/40 py-16 text-center">
            <i className="ri-emotion-laugh-line mx-auto block text-4xl text-foreground-400" aria-hidden="true" />
            <p className="mt-3 text-base text-foreground-600">这一趴暂时还没瓜，换个词或栏目试试～</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((g) => {
              const isLiked = !!liked[g.id];
              return (
                <article
                  key={g.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-100/40 transition-colors hover:border-accent-300"
                >
                  <div className="relative h-44 w-full overflow-hidden md:h-48">
                    <img
                      src={g.image}
                      alt={g.alt}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span
                      className={`absolute left-3 top-3 inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold ${
                        g.tag === "爆" || g.tag === "火"
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-50/90 text-primary-700"
                      }`}
                    >
                      <i className="ri-fire-fill" aria-hidden="true" />
                      {g.tag}
                    </span>
                    <span className="absolute right-3 top-3 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-600/90 px-2.5 py-1 text-xs font-semibold text-background-50 backdrop-blur">
                      <i className="ri-eye-line" aria-hidden="true" />
                      {g.online.toLocaleString()} 围观
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col p-4 md:p-5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1 rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-semibold text-secondary-800">
                        {g.category}
                      </span>
                      <span className="text-xs text-foreground-500">
                        <i className="ri-megaphone-line mr-1" aria-hidden="true" />
                        {g.author}
                      </span>
                    </div>
                    <h3 className="mt-3 text-base font-bold leading-snug text-foreground-950 md:text-lg">
                      {g.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{g.desc}</p>

                    <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-sm text-foreground-600">
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                        {g.heat.toFixed(1)} 热度
                      </span>
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-chat-3-line" aria-hidden="true" />
                        {g.comments.toLocaleString()}
                      </span>
                      <span className="hidden whitespace-nowrap text-xs text-foreground-500 sm:inline">{g.time}</span>
                      <button
                        type="button"
                        onClick={() => toggleLike(g)}
                        aria-label={isLiked ? "取消点赞" : "点赞"}
                        aria-pressed={isLiked}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                          isLiked
                            ? "bg-primary-100 text-primary-600"
                            : "text-foreground-400 hover:bg-secondary-100 hover:text-primary-500"
                        }`}
                      >
                        <i className={isLiked ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {filtered.length > visible && (
          <div className="mt-9 text-center">
            <button
              type="button"
              onClick={() => setVisible((v) => v + 6)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-100/60 px-6 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              <i className="ri-arrow-down-s-line text-lg" aria-hidden="true" />
              再看更多热瓜
            </button>
          </div>
        )}
      </div>
    </section>
  );
}